/**
 * Confidence-band + own-complex helpers for the Compare surface.
 *
 * These power two VALUE upgrades on the compare cards + detailed table:
 *
 *   1. Confidence bands - the model's predicted_price_low / _high range
 *      plus a high/medium/low confidence bucket, so a tight high-conf
 *      "fair" reads differently from a wide low-conf "great deal". A
 *      point estimate alone is false precision across units of differing
 *      data quality.
 *
 *   2. Deal-vs-own-complex - the payload already carries the matched
 *      residence's per-segment median AZN/m² (context.residence). When a
 *      listing matched a catalog residence we compute how far its own
 *      AZN/m² sits under/over the typical AZN/m² for the same room / floor /
 *      repair segment (falling back to the overall segment). This is the
 *      most decisive same-building price signal a buyer can get.
 *
 * Pure functions only - the same JSON must produce the same band /
 * delta on every render, mirroring metrics.ts. No LLM input.
 */
import type { AnalysisResult, ResidenceStats, ResidenceSegment } from "@/types";
import { pricePerM2 } from "./metrics";

/* ── Confidence bucket ── */

export type ConfidenceLevel = "high" | "medium" | "low";

/** Bucket the model's confidence_pct into high/medium/low. Mirrors the
 *  thresholds used by ResultHero so the compare surface agrees with the
 *  full analysis page. Null confidence reads as "medium" (the model
 *  returned a band but no explicit score) so we never dim a verdict on
 *  missing data alone. */
export function confidenceLevel(pct: number | null | undefined): ConfidenceLevel {
  if (pct == null) return "medium";
  if (pct >= 70) return "high";
  if (pct >= 50) return "medium";
  return "low";
}

/** A resolved confidence band for one listing. `hasBand` is false when
 *  the backend didn't ship a low/high pair - callers then fall back to a
 *  ±5 % synthetic range so the range chip still renders something
 *  sensible (matching VerdictGauge / ResultHero behaviour). `isLowConf`
 *  drives the dim/asterisk treatment on a low-confidence verdict. */
export interface ConfidenceBand {
  low: number;
  high: number;
  hasBand: boolean;
  confidencePct: number | null;
  level: ConfidenceLevel;
  isLowConf: boolean;
}

/** Resolve the displayed band for a listing. Prefers the adjusted band
 *  (post-LLM-penalty) when present, matching the fair-value point the
 *  cards/table already show (adjusted_predicted_price ?? predicted_price),
 *  then falls back to the raw model band, then to a ±5 % synthetic band.
 *
 *  Rounding is left to the caller (sale vs rent grain differs) - this
 *  returns raw AZN bounds. */
export function resolveBand(result: AnalysisResult): ConfidenceBand | null {
  const point = result.adjusted_predicted_price ?? result.predicted_price;
  if (!point || point <= 0) return null;

  const adjLow = result.adjusted_predicted_price_low;
  const adjHigh = result.adjusted_predicted_price_high;
  const rawLow = result.predicted_price_low;
  const rawHigh = result.predicted_price_high;

  let low: number;
  let high: number;
  let hasBand: boolean;
  if (adjLow != null && adjHigh != null) {
    low = adjLow;
    high = adjHigh;
    hasBand = true;
  } else if (rawLow != null && rawHigh != null) {
    low = rawLow;
    high = rawHigh;
    hasBand = true;
  } else {
    low = point * 0.95;
    high = point * 1.05;
    hasBand = false;
  }

  const confidencePct = result.confidence_pct ?? null;
  const level = confidenceLevel(confidencePct);
  return {
    low,
    high,
    hasBand,
    confidencePct,
    level,
    // Only dim/asterisk when the model itself reports low confidence -
    // a synthetic ±5 % band with no score stays "medium" and is not
    // dimmed (absence of a score is not the same as low confidence).
    isLowConf: confidencePct != null && level === "low",
  };
}

/** Round + format a band as "low - high" with a thin-space-free hyphen
 *  separator (house style: no em dashes). `fmt` is the caller's price
 *  formatter; `round` snaps each bound to the sale/rent grain so a tight
 *  band doesn't collapse to "1,000 - 1,000". */
export function formatBand(
  band: ConfidenceBand,
  fmt: (n: number) => string,
  round: (n: number) => number,
): string {
  return `${fmt(round(band.low))} - ${fmt(round(band.high))}`;
}

/* ── Deal-vs-own-complex ── */

/** Which residence segment a listing's own AZN/m² was compared against.
 *  Surfaced so the UI / tooltip can say WHICH slice of the building the
 *  comparison used ("same rooms", "top floor", "renovated", "building
 *  overall"). */
export type ResidenceSegmentKind =
  | "rooms"
  | "first_floor"
  | "last_floor"
  | "with_repair"
  | "no_repair"
  | "overall";

export interface OwnComplexDelta {
  /** Signed % the listing's own AZN/m² sits relative to the segment
   *  median. Negative = under the building's typical (a deal), positive
   *  = over. */
  pct: number;
  /** Median AZN/m² of the matched segment. */
  segmentPpm: number;
  /** The listing's own AZN/m². */
  ownPpm: number;
  /** Which segment the comparison used. */
  segment: ResidenceSegmentKind;
  /** Listing count behind the matched segment - thin segments (n is
   *  small) are less trustworthy, surfaced as a caption. */
  n: number;
  /** Residence display name. */
  name: string;
}

/** Minimum listings for a per-attribute segment to be trusted. Below
 *  this we fall back toward the overall segment, which always has the
 *  largest sample. Keeps a 1-listing "rooms_4plus" segment from
 *  producing a wild, meaningless delta. */
const MIN_SEGMENT_N = 3;

function usableSegment(seg: ResidenceSegment | undefined): seg is ResidenceSegment {
  return (
    !!seg &&
    typeof seg.m === "number" &&
    Number.isFinite(seg.m) &&
    seg.m > 0 &&
    typeof seg.n === "number" &&
    seg.n >= MIN_SEGMENT_N
  );
}

/** Pick the room-bucket segment key for a listing's room count. 4+ rooms
 *  collapse into the rooms_4plus bucket, matching the backend
 *  aggregation. */
function roomSegmentKey(
  rooms: number | null | undefined,
): keyof ResidenceStats["segments"] | null {
  if (rooms == null || !Number.isFinite(rooms) || rooms < 1) return null;
  if (rooms >= 4) return "rooms_4plus";
  return (`rooms_${rooms}` as keyof ResidenceStats["segments"]);
}

/**
 * Compute how far a listing's own AZN/m² sits under / over its matched
 * building's typical AZN/m². Returns null when the listing has no matched
 * residence, no usable segment, or no own AZN/m² (missing price/area).
 *
 * Segment selection (most-specific decisive slice first, each gated on a
 * usable sample size, else fall through):
 *   1. same room count
 *   2. floor band (first / last floor) when the listing is on one
 *   3. renovation match (with_repair / no_repair)
 *   4. building overall
 *
 * Rooms come first because a same-building, same-room comparable is the
 * tightest like-for-like a buyer can get; floor + repair refine only
 * when the room segment is too thin to trust.
 */
export function ownComplexDelta(result: AnalysisResult): OwnComplexDelta | null {
  const residence: ResidenceStats | null | undefined = result.context?.residence;
  if (!residence || !residence.segments) return null;

  const ownPpm = pricePerM2(result);
  if (ownPpm == null || ownPpm <= 0) return null;

  const segs = residence.segments;

  // Build the preference ladder. Floor band only enters when the
  // listing actually sits on the first or top floor; repair band only
  // when has_repair is known.
  const candidates: Array<{ kind: ResidenceSegmentKind; seg: ResidenceSegment | undefined }> = [];

  const roomKey = roomSegmentKey(result.room_count);
  if (roomKey) candidates.push({ kind: "rooms", seg: segs[roomKey] });

  if (result.floor != null && result.floor === 1) {
    candidates.push({ kind: "first_floor", seg: segs.first_floor });
  }
  if (
    result.floor != null &&
    result.total_floors != null &&
    result.floor === result.total_floors
  ) {
    candidates.push({ kind: "last_floor", seg: segs.last_floor });
  }

  if (result.has_repair === true) {
    candidates.push({ kind: "with_repair", seg: segs.with_repair });
  } else if (result.has_repair === false) {
    candidates.push({ kind: "no_repair", seg: segs.no_repair });
  }

  candidates.push({ kind: "overall", seg: segs.overall });

  const picked = candidates.find((c) => usableSegment(c.seg));
  if (!picked || !picked.seg) return null;

  const segmentPpm = picked.seg.m;
  const pct = ((ownPpm - segmentPpm) / segmentPpm) * 100;

  return {
    pct,
    segmentPpm,
    ownPpm,
    segment: picked.kind,
    n: picked.seg.n,
    name: residence.name,
  };
}
